import React, { useState } from 'react';
import Modal from '../../Modal';
import {
    RESOURCE_TYPES,
    RESOURCE_TYPE_LABELS,
    guessTimezone,
} from './bookingFormat';

// A short, non-exhaustive list of common zones to offer via <datalist>; the
// field stays free-text so any valid IANA zone works (backend validates).
const COMMON_TIMEZONES = [
    'America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles',
    'America/Phoenix', 'America/Anchorage', 'Pacific/Honolulu', 'America/Toronto',
    'America/Sao_Paulo', 'Europe/London', 'Europe/Paris', 'Europe/Berlin',
    'Europe/Madrid', 'Europe/Athens', 'Africa/Johannesburg', 'Asia/Dubai',
    'Asia/Kolkata', 'Asia/Singapore', 'Asia/Tokyo', 'Australia/Sydney', 'UTC',
];

/**
 * Create/edit form for a booking resource (calendar). Semi-independent: it owns
 * no data, just an initial resource and an onSubmit that persists. Can be
 * launched standalone from any context.
 */
export default function ResourceEditorModal({
    resource,
    defaultTimezone = null,
    onSubmit,
    onClose,
}) {
    const isEdit = !!(resource && resource.id);
    const [form, setForm] = useState(() => ({
        name: resource?.name || '',
        type: resource?.type || 'seller',
        capacity: resource?.capacity ?? 1,
        timezone: resource?.timezone || defaultTimezone || guessTimezone(),
        color: resource?.color || '#4f46e5',
        location: resource?.location || '',
        description: resource?.description || '',
        isActive: resource?.isActive ?? true,
        publicBooking: {
            enabled: resource?.settings?.publicBooking?.enabled || false,
            durationMinutes: resource?.settings?.publicBooking?.durationMinutes ?? 30,
            granularityMinutes: resource?.settings?.publicBooking?.granularityMinutes ?? 30,
            minNoticeMinutes: resource?.settings?.publicBooking?.minNoticeMinutes ?? 60,
            maxAdvanceDays: resource?.settings?.publicBooking?.maxAdvanceDays ?? 60,
            confirmation: resource?.settings?.publicBooking?.confirmation || 'instant',
            timezoneDisplay: resource?.settings?.publicBooking?.timezoneDisplay || 'shopper',
        },
        localPickup: {
            enabled: resource?.settings?.localPickup?.enabled ?? true,
            windowMinutes: resource?.settings?.localPickup?.windowMinutes ?? 30,
            minNoticeMinutes: resource?.settings?.localPickup?.minNoticeMinutes ?? 60,
            maxAdvanceDays: resource?.settings?.localPickup?.maxAdvanceDays ?? 60,
            timezoneDisplay: resource?.settings?.localPickup?.timezoneDisplay || 'resource',
        },
    }));
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    const setField = (name, value) => setForm(prev => ({ ...prev, [name]: value }));
    const setPublicBookingField = (name, value) => setForm(prev => ({
        ...prev,
        publicBooking: { ...prev.publicBooking, [name]: value },
    }));
    const setLocalPickupField = (name, value) => setForm(prev => ({
        ...prev,
        localPickup: { ...prev.localPickup, [name]: value },
    }));

    const handleSubmit = async (event) => {
        event.preventDefault();
        setError(null);
        if (!form.name.trim()) {
            setError('Please give this calendar a name.');
            return;
        }
        setSaving(true);
        try {
            await onSubmit({
                name: form.name.trim(),
                type: form.type,
                capacity: parseInt(form.capacity, 10) || 1,
                timezone: form.timezone.trim(),
                color: form.color || null,
                location: form.location.trim() || null,
                description: form.description.trim() || null,
                publicBooking: {
                    ...form.publicBooking,
                    durationMinutes: parseInt(form.publicBooking.durationMinutes, 10) || 30,
                    granularityMinutes: parseInt(form.publicBooking.granularityMinutes, 10)
                        || parseInt(form.publicBooking.durationMinutes, 10)
                        || 30,
                    minNoticeMinutes: parseInt(form.publicBooking.minNoticeMinutes, 10) || 0,
                    maxAdvanceDays: parseInt(form.publicBooking.maxAdvanceDays, 10) || 60,
                },
                ...(form.type === 'pickup' ? {
                    localPickup: {
                        ...form.localPickup,
                        windowMinutes: parseInt(form.localPickup.windowMinutes, 10) || 30,
                        minNoticeMinutes:
                            parseInt(form.localPickup.minNoticeMinutes, 10) || 0,
                        maxAdvanceDays:
                            parseInt(form.localPickup.maxAdvanceDays, 10) || 60,
                    },
                } : {}),
                ...(isEdit ? { isActive: form.isActive } : {}),
            });
            onClose();
        } catch (err) {
            setError(err.message || 'Could not save this calendar.');
            setSaving(false);
        }
    };

    return (
        <Modal
            isOpen
            onClose={onClose}
            title={isEdit ? 'Edit calendar' : 'New calendar'}
            size="medium"
            className="booking-modal"
        >
            <form className="booking-form" onSubmit={handleSubmit}>
                {error && <div className="error booking-form-error">{error}</div>}

                <label className="booking-field">
                    <span>Name</span>
                    <input
                        type="text"
                        value={form.name}
                        onChange={e => setField('name', e.target.value)}
                        placeholder="e.g. Stylist Jane, Cabin #3, Pickup counter"
                        maxLength={120}
                        autoFocus
                    />
                </label>

                <div className="booking-field-row">
                    <label className="booking-field">
                        <span>Type</span>
                        <select value={form.type} onChange={e => setField('type', e.target.value)}>
                            {RESOURCE_TYPES.map(type => (
                                <option key={type} value={type}>{RESOURCE_TYPE_LABELS[type]}</option>
                            ))}
                        </select>
                    </label>

                    <label className="booking-field">
                        <span>Capacity</span>
                        <input
                            type="number"
                            min={1}
                            max={10000}
                            value={form.capacity}
                            onChange={e => setField('capacity', e.target.value)}
                        />
                        <small className="help">Concurrent bookings per slot (group size / units).</small>
                    </label>
                </div>

                <div className="booking-field-row">
                    <label className="booking-field">
                        <span>Timezone</span>
                        <input
                            type="text"
                            list="booking-tz-list"
                            value={form.timezone}
                            onChange={e => setField('timezone', e.target.value)}
                            placeholder="America/New_York"
                            required
                        />
                        <datalist id="booking-tz-list">
                            {COMMON_TIMEZONES.map(tz => <option key={tz} value={tz} />)}
                        </datalist>
                        <small className="help">
                            Weekly hours, blackouts, and openings use this timezone.
                        </small>
                    </label>

                    <label className="booking-field booking-field--color">
                        <span>Color</span>
                        <input
                            type="color"
                            value={form.color || '#4f46e5'}
                            onChange={e => setField('color', e.target.value)}
                        />
                    </label>
                </div>

                <label className="booking-field">
                    <span>Location <small className="help">(optional)</small></span>
                    <input
                        type="text"
                        value={form.location}
                        onChange={e => setField('location', e.target.value)}
                        placeholder="123 Main St, or a room name"
                    />
                </label>

                <label className="booking-field">
                    <span>Notes <small className="help">(optional)</small></span>
                    <textarea
                        value={form.description}
                        onChange={e => setField('description', e.target.value)}
                        rows={2}
                        maxLength={255}
                    />
                </label>

                {form.type === 'pickup' && (
                    <fieldset className="booking-public-settings">
                        <legend>Local pickup scheduling</legend>
                        <label className="checkbox" htmlFor="booking-resource-pickup">
                            <input
                                id="booking-resource-pickup"
                                type="checkbox"
                                checked={form.localPickup.enabled}
                                onChange={event => (
                                    setLocalPickupField('enabled', event.target.checked)
                                )}
                            />
                            <span>Require a time for Local Pickup orders</span>
                        </label>
                        <p className="help">
                            Shoppers choosing Local Pickup reserve this calendar during
                            checkout. Set the hours with the Availability button.
                        </p>
                        {form.localPickup.enabled && (
                            <div className="booking-public-settings-fields">
                                <div className="booking-field-row">
                                    <label className="booking-field">
                                        <span>Pickup window</span>
                                        <select
                                            value={form.localPickup.windowMinutes}
                                            onChange={event => setLocalPickupField(
                                                'windowMinutes',
                                                event.target.value,
                                            )}
                                        >
                                            <option value={15}>15 minutes</option>
                                            <option value={30}>30 minutes</option>
                                            <option value={45}>45 minutes</option>
                                            <option value={60}>60 minutes</option>
                                        </select>
                                    </label>
                                    <label className="booking-field">
                                        <span>Minimum notice (minutes)</span>
                                        <input
                                            type="number"
                                            min={0}
                                            value={form.localPickup.minNoticeMinutes}
                                            onChange={event => setLocalPickupField(
                                                'minNoticeMinutes',
                                                event.target.value,
                                            )}
                                        />
                                    </label>
                                </div>
                                <div className="booking-field-row">
                                    <label className="booking-field">
                                        <span>Maximum advance booking (days)</span>
                                        <input
                                            type="number"
                                            min={1}
                                            value={form.localPickup.maxAdvanceDays}
                                            onChange={event => setLocalPickupField(
                                                'maxAdvanceDays',
                                                event.target.value,
                                            )}
                                        />
                                    </label>
                                    <label className="booking-field">
                                        <span>Show times in</span>
                                        <select
                                            value={form.localPickup.timezoneDisplay}
                                            onChange={event => setLocalPickupField(
                                                'timezoneDisplay',
                                                event.target.value,
                                            )}
                                        >
                                            <option value="resource">
                                                Pickup location timezone
                                            </option>
                                            <option value="shopper">Shopper’s timezone</option>
                                        </select>
                                    </label>
                                </div>
                            </div>
                        )}
                    </fieldset>
                )}

                <fieldset className="booking-public-settings">
                    <legend>Public booking link</legend>
                    <label className="checkbox" htmlFor="booking-resource-public">
                        <input
                            id="booking-resource-public"
                            type="checkbox"
                            checked={form.publicBooking.enabled}
                            onChange={e => setPublicBookingField('enabled', e.target.checked)}
                        />
                        <span>Let anyone book this calendar from my storefront</span>
                    </label>
                    <p className="help">
                        This creates a shareable, product-free “grab a meeting” page.
                    </p>

                    {form.publicBooking.enabled && (
                        <div className="booking-public-settings-fields">
                            <div className="booking-field-row">
                                <label className="booking-field">
                                    <span>Meeting length</span>
                                    <select
                                        value={form.publicBooking.durationMinutes}
                                        onChange={e => {
                                            setPublicBookingField(
                                                'durationMinutes',
                                                e.target.value,
                                            );
                                            setPublicBookingField(
                                                'granularityMinutes',
                                                e.target.value,
                                            );
                                        }}
                                    >
                                        <option value={15}>15 minutes</option>
                                        <option value={30}>30 minutes</option>
                                        <option value={45}>45 minutes</option>
                                        <option value={60}>60 minutes</option>
                                        <option value={90}>90 minutes</option>
                                        <option value={120}>2 hours</option>
                                    </select>
                                </label>

                                <label className="booking-field">
                                    <span>Confirmation</span>
                                    <select
                                        value={form.publicBooking.confirmation}
                                        onChange={e => setPublicBookingField(
                                            'confirmation',
                                            e.target.value,
                                        )}
                                    >
                                        <option value="instant">Confirm instantly</option>
                                        <option value="request">Require my approval</option>
                                    </select>
                                </label>
                            </div>

                            <div className="booking-field-row">
                                <label className="booking-field">
                                    <span>Minimum notice (minutes)</span>
                                    <input
                                        type="number"
                                        min={0}
                                        max={129600}
                                        value={form.publicBooking.minNoticeMinutes}
                                        onChange={e => setPublicBookingField(
                                            'minNoticeMinutes',
                                            e.target.value,
                                        )}
                                    />
                                </label>
                                <label className="booking-field">
                                    <span>Book up to (days ahead)</span>
                                    <input
                                        type="number"
                                        min={1}
                                        max={730}
                                        value={form.publicBooking.maxAdvanceDays}
                                        onChange={e => setPublicBookingField(
                                            'maxAdvanceDays',
                                            e.target.value,
                                        )}
                                    />
                                </label>
                            </div>

                            <label className="booking-field">
                                <span>Show times in</span>
                                <select
                                    value={form.publicBooking.timezoneDisplay}
                                    onChange={e => setPublicBookingField(
                                        'timezoneDisplay',
                                        e.target.value,
                                    )}
                                >
                                    <option value="shopper">Visitor’s timezone</option>
                                    <option value="resource">Calendar timezone</option>
                                </select>
                            </label>
                        </div>
                    )}
                </fieldset>

                {isEdit && (
                    <label className="checkbox" htmlFor="booking-resource-active">
                        <input
                            id="booking-resource-active"
                            type="checkbox"
                            checked={form.isActive}
                            onChange={e => setField('isActive', e.target.checked)}
                        />
                        <span>Active (available for new bookings)</span>
                    </label>
                )}

                <div className="booking-form-actions">
                    <button type="button" className="button secondary" onClick={onClose} disabled={saving}>
                        Cancel
                    </button>
                    <button type="submit" className="button primary" disabled={saving}>
                        {saving ? 'Saving…' : (isEdit ? 'Save changes' : 'Create calendar')}
                    </button>
                </div>
            </form>
        </Modal>
    );
}
